"use client";

import React, { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { cn } from "@/lib/utils";
const headerMenuItems = [{
  id: "home",
  label: "Acasă",
  href: "/#performanță-fără-compromis"
}, {
  id: "services",
  label: "Servicii",
  href: "/#expertiza-noastră-moto"
}, {
  id: "contact",
  label: "Contact",
  href: "/#vino-în-service"
}, {
  id: "history",
  label: "Despre noi",
  href: "/#pasiune-și-experiență"
}, {
  id: "parts",
  label: "Piese",
  href: "/#piesemotopromotional"
}];
function DesktopMenuConsumer() {
  return <NavigationMenu viewport={false} className="hidden lg:flex">
      <NavigationMenuList className="gap-4 lg:gap-8">
        {headerMenuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="text-foreground font-default font-semibold text-sm tracking-wider uppercase transition-colors hover:text-primary relative after:absolute after:-bottom-1 after:left-0 after:h-[2px] after:w-full after:origin-bottom-right after:scale-x-0 after:bg-primary after:transition-transform hover:after:origin-bottom-left hover:after:scale-x-100 outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-sm">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  isOpen,
  onClose
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  return <nav className={cn("absolute top-full left-0 w-full bg-background/95 backdrop-blur-xl border-b border-border shadow-2xl transition-all duration-300 origin-top overflow-hidden lg:hidden", isOpen ? "max-h-[600px] opacity-100" : "max-h-0 opacity-0")}>
      <div className="flex flex-col px-6 py-4">
        {headerMenuItems.map((item, i) => <Link key={item.id} to={item.href || "#"} data-index={i} onClick={onClose} className="py-4 text-foreground font-default font-bold text-base border-b border-border/50 last:border-0 hover:text-primary transition-colors uppercase tracking-wide">
            {item.label}
          </Link>)}
      </div>
    </nav>;
}
export default function InstitutionalHeader() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          if (hash === "#performanță-fără-compromis") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            setIsMobileMenuOpen(false);
            return;
          }
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            const headerOffset = 80;
            const top = element.getBoundingClientRect().top + window.scrollY - headerOffset;
            window.scrollTo({
              top,
              behavior: "smooth"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section id="header" className={cn("sticky top-0 z-50 w-full transition-all duration-300 bg-background text-foreground", isScrolled ? "bg-background/90 backdrop-blur-md border-b border-border shadow-md" : "bg-background border-b border-transparent")}>
      <div className="max-w-7xl mx-auto px-6 md:px-8">
        <div className="flex items-center justify-between h-[80px]">
          <div className="flex-shrink-0">
            <Link to="/" className="block outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-sm">
              <img src="https://grateful-javelin.10web.cloud/wp-content/uploads/2026/04/Finished-Logo.png" alt="Pure Moto Performance" className="object-contain !h-[90px] !w-[100px]" />
            </Link>
          </div>

          <DesktopMenuConsumer />

          <div className="flex items-center gap-3 md:gap-4">
            <Button asChild className="bg-primary hover:bg-primary/90 text-primary-foreground font-default font-bold uppercase tracking-wider rounded-md shadow-lg transition-transform active:scale-95 px-4 py-2 h-9 md:h-10 md:px-6">
              <Link to="tel:+40745236441">Sună Acum</Link>
            </Button>

            <div className="lg:hidden flex items-center">
              <Button variant="ghost" size="icon" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle Menu" className="text-foreground hover:bg-muted hover:text-primary">
                {isMobileMenuOpen ? <X className="size-6" /> : <Menu className="size-6" />}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <MobileMenuConsumer isOpen={isMobileMenuOpen} onClose={() => setIsMobileMenuOpen(false)} />
    </section>;
}